/**
 * @file: cubeTree 高级查询
 * @author: xiaolong.huang@dmall.com
 */

import Utils from '../utils/util';
import { getCubeValue } from './getCubeValue';
import {
    isShelf,
    isFreezer,
    isInStage,
    isPartition,
    isGoods,
    isBafflePlate,
    isTextarea,
    isEmptyShelf,
    isLayer,
    isBeam,
    isDishrack,
    isCabinet
} from './cubeInfo';
import {
    getCubeByType,
    findBrothersContainSelfByCubeId,
    findByCubeId,
    findChildCubeIds,
    findParentIdByCubeId,
    getCubeIndexInParent,
    findChildCubeByIndex,
    getAllCube,
    findCubeByType,
    findChildCubeIdsWithSkip
} from './baseQuery';

// 获取画布中所有的立式货架
function findShelfs(): Array<CubeId> {
    let result: Array<CubeId> = [];
    let sketchpadId: CubeId = findSketchpad();
    let childIds: Array<CubeId> = findChildCubeIds(sketchpadId);

    result = childIds.filter((cubeId: CubeId): boolean => {
        return isShelf(cubeId);
    });

    return result;
}

// 获取画布中所有的卧柜
function findFreezer(): Array<CubeId> {
    let result: Array<CubeId> = [];
    let sketchpadId: CubeId = findSketchpad();
    let childIds: Array<string> = findChildCubeIds(sketchpadId);

    result = childIds.filter((cubeId: CubeId): boolean => {
        return isFreezer(cubeId);
    });

    return result;
}

// 查找前一个货架
function findPrevShelf(shelfId: CubeId): CubeId {
    let shelfs: Array<CubeId> = findShelfs();

    for(let i: number = 0; i < shelfs.length; i++) {
        if (shelfs[i] === shelfId && i > 0) {
            return shelfs[i - 1];
        }
    }

    return '';
}

// 查找前一个卧柜
function findPrevFreezer(freezerId: CubeId): CubeId|null {
    let freezers: Array<CubeId> = findFreezer();

    for(let i: number = 0; i < freezers.length; i++) {
        if (freezers[i] === freezerId && i > 0) {
            return freezers[i - 1];
        }
    }

    return null;
}

// 查找下一个卧柜
function findNextFreezer(freezerId: CubeId): CubeId|null {
    let freezers: Array<CubeId> = findFreezer();

    for(let i: number = 0; i < freezers.length; i++) {
        if (freezers[i] === freezerId && i < freezers.length - 1) {
            return freezers[i + 1];
        }
    }

    return null;
}

// 查找下一个货架
function findNextShelf(shelfId: CubeId): CubeId|null {
    let shelfs: Array<CubeId> = findShelfs();

    for(let i: number = 0; i < shelfs.length; i++) {
        if (shelfs[i] === shelfId && i < shelfs.length - 1) {
            return shelfs[i + 1];
        }
    }

    return null;
}

// 查询画布上非货架组件
function findCubeInStage(): Array<CubeId> {
    let result: Array<CubeId> = [];
    let sketchpadId: CubeId = findSketchpad();
    let childIds: Array<CubeId> = findChildCubeIds(sketchpadId);

    result = childIds.filter((cubeId: CubeId): boolean => {
        return !isShelf(cubeId) && !isFreezer(cubeId);
    });

    return result;
}

// 查找层板的前一个层板
function findPrevLayer(layerId: CubeId): CubeId|null {
    let result: CubeId|null = null;
    let brotherLayer: Array<CubeId> = findBrothersContainSelfByCubeId(layerId);
    let temp: Array<FreeObjMap> = brotherLayer.map((layerId: CubeId): FreeObjMap => {
        let layer: CubeNode = findByCubeId(layerId);

        return {
            layerId,
            posY: Utils.toNumber(getCubeValue(layer, 'posY'))
        };
    });

    // 按照posY升序排列
    let sortTemp: Array<FreeObjMap> = Utils.quickSortByKey(temp, 'posY');

    for (let i = 0; i < sortTemp.length; i++) {
        if (sortTemp[i].layerId === layerId && i > 0) {
            result = sortTemp[i - 1].layerId;
            break;
        }
    }

    return result;
}

// 查找层板的后一个层板
function findNextLayer(layerId: CubeId): CubeId|null {
    let result: CubeId|null = null;
    let brotherLayer: Array<CubeId> = findBrothersContainSelfByCubeId(layerId);
    let temp: Array<FreeObjMap> = brotherLayer.map((layerId: CubeId): FreeObjMap => {
        let layer: CubeNode = findByCubeId(layerId);

        return {
            layerId,
            posY: Utils.toNumber(getCubeValue(layer, 'posY'))
        };
    });
    let sortTemp: Array<FreeObjMap> = Utils.quickSortByKey(temp, 'posY');

    for (let i = 0; i < sortTemp.length; i++) {
        if (sortTemp[i].layerId === layerId && (i + 1) <= sortTemp.length - 1) {
            result = sortTemp[i + 1].layerId;
            break;
        }
    }

    return result;
}

// 查找最后一个层板，层板从下往上计数
function findTheLastLayer(shelfId: CubeId): CubeId {
    let result: CubeId = '';
    let layers: Array<CubeId> = findChildCubeIds(shelfId);
    let temp: Array<FreeObjMap> = layers.map((layerId: CubeId): FreeObjMap => {
        let layer: CubeNode = findByCubeId(layerId);

        return {
            layerId,
            posY: Utils.toNumber(getCubeValue(layer, 'posY'))
        };
    });

    // 按照posY升序排列
    let sortTemp: Array<FreeObjMap> = Utils.quickSortByKey(temp, 'posY');

    if (sortTemp.length) {
        result = sortTemp[sortTemp.length - 1].layerId;
    }

    return result;
}

// 查找倒数第二个层板
function findLastPrevLayer(shelfId: CubeId): CubeId|null {
    if (!isShelf(shelfId) && !isFreezer(shelfId)) {
        return null;
    }

    if (isEmptyShelf(shelfId)) {
        return null;
    }

    if (shelfHasOnlyOneLayer(shelfId)) {
        return null;
    }

    let result: CubeId = '';
    let layers: Array<CubeId> = findChildCubeIds(shelfId);
    let temp: Array<FreeObjMap> = layers.map((layerId: CubeId): FreeObjMap => {
        let layer: CubeNode = findByCubeId(layerId);

        return {
            layerId,
            posY: Utils.toNumber(getCubeValue(layer, 'posY'))
        };
    });

    // 按照posY升序排列
    let sortTemp: Array<FreeObjMap> = Utils.quickSortByKey(temp, 'posY');

    return sortTemp[sortTemp.length - 2].layerId;
}

// 计算层板相邻两个层板
function findClosestLayer(layerId: CubeId): Array<CubeId> {
    let result: Array<CubeId> = [];
    let brothersLayerIds: Array<CubeId> = findBrothersContainSelfByCubeId(layerId);
    let temp: Array<FreeObjMap> = brothersLayerIds.map((cubeId: CubeId): FreeObjMap => {
        let cube: CubeNode = findByCubeId(cubeId);

        return {
            cubeId,
            posY: Utils.toNumber(getCubeValue(cube, 'posY'))
        };
    });
    let sortTemp: Array<FreeObjMap> = Utils.quickSortByKey(temp, 'posY');

    for (let i = 0; i < sortTemp.length; i++) {
        if (sortTemp[i].cubeId !== layerId) {
            continue;
        }

        if (i > 0) {
            result.push(sortTemp[i - 1].cubeId);
        }

        if (i < sortTemp.length - 1) {
            result.push(sortTemp[i + 1].cubeId);
        }
    }

    return result;
}

// 计算层板相邻两个层板, 包含自身
function findClosestLayerContainSelf(layerId: CubeId): Array<CubeId> {
    let result: Array<CubeId> = [];
    let brothersLayerIds: Array<CubeId> = findBrothersContainSelfByCubeId(layerId);
    let temp: Array<FreeObjMap> = brothersLayerIds.map((cubeId: CubeId): FreeObjMap => {
        let cube: CubeNode = findByCubeId(cubeId);

        return {
            cubeId,
            posY: Utils.toNumber(getCubeValue(cube, 'posY'))
        };
    });
    let sortTemp: Array<FreeObjMap> = Utils.quickSortByKey(temp, 'posY');

    for (let i = 0; i < sortTemp.length; i++) {
        if (sortTemp[i].cubeId !== layerId) {
            continue;
        }

        if (i > 0) {
            result.push(sortTemp[i - 1].cubeId);
        }

        result.push(layerId);

        if (i < sortTemp.length - 1) {
            result.push(sortTemp[i + 1].cubeId);
        }
    }

    return result;
}

// 层板是否是第一个层板
function isLastLayer(layerId: CubeId): boolean {
    let shelfId: CubeId = findParentIdByCubeId(layerId);

    return findTheLastLayer(shelfId) === layerId;
}

// 查询画布, 画布是唯一的，不用返回列表，直接范围cubeid即可
function findSketchpad(): CubeId {
    return getCubeByType('sketchpad')[0];
}

/**
 * 计算图纸上的货架数量
 */
function getShelfCount(): number {
    return getCubeByType('shelf').length;
}

function getFreezerCount(): number {
    return getCubeByType('freezer').length;
}

// 查找最后一个货架
function findLastShelf(skipShelfs: Array<CubeId> = []): CubeId|null {
    let shelfs: Array<CubeId> = findShelfs();
    let skip: Set<CubeId> = new Set(skipShelfs);

    for(let i = shelfs.length - 1; i >=0; i--) {
        if (skip.has(shelfs[i])) {
            continue;
        }

        return shelfs[i];
    }

    return null;
}

// 查找cube所在货架
function findCubeShelf(cubeId: CubeId): CubeId|null {
    if (isInStage(cubeId)) {
        return null;
    }

    if (isPartition(cubeId)) {
        return findParentIdByCubeId(cubeId);
    }

    if (isGoods(cubeId)
        || isBafflePlate(cubeId)
        || isTextarea(cubeId)) {
        let layer: CubeId = findParentIdByCubeId(cubeId);

        return findParentIdByCubeId(layer);
    }

    return null;
}

// 查找cube所在货架
function findCubeFreezer(cubeId: CubeId): CubeId|null {
    if (isInStage(cubeId)) {
        return null;
    }

    if (isCabinet(cubeId)) {
        return findParentIdByCubeId(cubeId);
    }

    if (isGoods(cubeId) || isTextarea(cubeId)) {
        let cabinet: CubeId = findParentIdByCubeId(cubeId);

        return findParentIdByCubeId(cabinet);
    }

    return null;
}

// 查找商品、隔板、文本框所在层板
function findCubeLayer(cubeId: CubeId): CubeId|null {
    let parentId: CubeId = findParentIdByCubeId(cubeId);

    if (isPartition(parentId)) {
        return parentId;
    }

    return null;
}

// 货架是否只有一个层板
function shelfHasOnlyOneLayer(cubeId: CubeId): boolean {
    if (!isShelf(cubeId) && !isFreezer(cubeId)) {
        return false;
    }

    let childIds: Array<CubeId> = findChildCubeIds(cubeId);

    return childIds.length === 1;
}

// 查找第一个子cube
function findTheFirstChildCubeId(parentId: CubeId, skip: CubeId[] = []): CubeId|null {
    let childIds: Array<CubeId> = findChildCubeIds(parentId);

    return Utils.findListFirstItem(childIds, skip);
}

// 查找最后一个子cube
function findTheLastChildCubeId(parentId: CubeId): CubeId|null {
    let childIds: Array<CubeId> = findChildCubeIds(parentId);

    if (!childIds.length) {
        return null;
    }

    return childIds[childIds.length - 1];
}

// 查询目标cube的所有子级, 不包含自身
function findAllChildren(cubeId: CubeId): Array<CubeId> {
    let result: Array<CubeId> = [];
    let children: Array<CubeId> = findChildCubeIds(cubeId);

    children.forEach((childId: CubeId) => {
        result.push(childId);
        result = result.concat(findAllChildren(childId));
    });

    return result;
}

// 查询目标cube的所有子级, 包含自身
function findAllChildrenContainSelf(cubeId: CubeId): Array<CubeId> {
    let result: Array<CubeId> = [cubeId];
    let children: Array<CubeId> = findChildCubeIds(cubeId);

    children.forEach((childId: CubeId) => {
        result.push(childId);
        result = result.concat(findAllChildren(childId));
    });

    return result;
}

// 查询目标cube的所有子级, 包含自身
function findAllChildrenContainSelfByCubeIds(cubeIds: Array<CubeId>): Array<CubeId> {
    let result: Array<CubeId> = [];

    cubeIds.forEach((cubeId: CubeId): void => {
        result = result.concat(findAllChildrenContainSelf(cubeId));
    });

    return Utils.unique(result);
}

// 查找前一个cube
function findPrevCube(targetCube: CubeId, skip: Array<CubeId> = []): CubeId|null {
    let result: CubeId|null = null;
    let skipList: Set<CubeId> = new Set(skip);
    let brotherCubes: Array<CubeId> = findBrothersContainSelfByCubeId(targetCube);
    let startIndex: number = getCubeIndexInParent(targetCube);

    for(let i = startIndex; i >= 0; i--) {
        if (brotherCubes[i] === targetCube) {
            continue;
        }

        if (skipList.has(brotherCubes[i])) {
            continue;
        }

        result = brotherCubes[i];
        break;
    }

    return result;
}

// 查找后一个cube
function findNextCube(targetCube: CubeId, skip: Array<CubeId> = []): CubeId|null {
    let result: CubeId|null = null;
    let skipList: Set<CubeId> = new Set(skip);
    let brotherCubes: Array<CubeId> = findBrothersContainSelfByCubeId(targetCube);
    let startIndex: number = getCubeIndexInParent(targetCube);

    for(let i = startIndex; i < brotherCubes.length; i++) {
        if (brotherCubes[i] === targetCube) {
            continue;
        }

        if (skipList.has(brotherCubes[i])) {
            continue;
        }

        result = brotherCubes[i];
        break;
    }

    return result;
}

// 根据索引查找cube，可以指定需要跳过的cube
function findChildCubeByIndexSkip(parentId: CubeId, index: number, skip: Array<CubeId> = []): CubeId|null {
    let skipList: Set<CubeId> = new Set(skip);
    let targetCube: CubeId|null = findChildCubeByIndex(parentId, index);

    if (skipList.has(targetCube)) {
        return findNextCube(targetCube, skip);
    }

    return targetCube;
}

// 查询所有层板
function findAllPartitions(): Array<CubeId> {
    let result: Set<CubeId> = new Set();
    let allCubes: Array<CubeId> = getAllCube();

    allCubes.forEach((cubeId: CubeId): void => {
        if (isPartition(cubeId)) {
            result.add(cubeId);
        }
    });

    return Array.from(result);
}

// 查询画布中可以放置组件的层板
function findLayersCanDropInStage(): CubeId[] {
    let result: Array<CubeId> = [];
    let sketchpadId: CubeId = findSketchpad();
    let childIds: Array<CubeId> = findChildCubeIds(sketchpadId);

    result = childIds.filter((cubeId: CubeId): boolean => {
        return isLayer(cubeId) || isBeam(cubeId) || isDishrack(cubeId);
    });

    return result;
}

function findParentsChild(parents: CubeId[]): CubeId[] {
    let result: CubeId[] = [];

    parents.forEach((parent: CubeId): void => {
        let children: CubeId[] = findChildCubeIds(parent);

        result = result.concat(children);
    });

    return result;
}

function hasBeam(cubeIds: CubeId[]): boolean {
    return cubeIds.some((cubeId: CubeId): boolean => {
        return isBeam(cubeId);
    });
}

function findCubesChildren(parents: CubeId[]): CubeId[] {
    let result: CubeId[] = [];

    parents.forEach((parent: CubeId): void => {
        let children: CubeId[] = findChildCubeIds(parent);

        result = result.concat(children);
    });

    return result;
}

function isOnlyOneShelf(cubeId: CubeId): boolean {
    let shelfs: CubeId[] = findShelfs();

    return shelfs.length === 1 && shelfs[0] === cubeId;
}

function isOnlyOneFreezer(cubeId: CubeId): boolean {
    let freezers: CubeId[] = findFreezer();

    return freezers.length === 1 && freezers[0] === cubeId;
}

// cube列表中是否存在货架类型的cube
function cubeListHasShelf(cubeIds: CubeId[]): boolean {
    for (let i = 0; i < cubeIds.length; i++) {
        if (isShelf(cubeIds[i])) {
            return true;
        }
    }

    return false;
}

function findLayersCanDrop(): CubeId[] {
    let result: Array<CubeId> = [];

    result = findCubeByType('layer');
    result = result.concat(findCubeByType('beam'));
    result = result.concat(findCubeByType('dishrack'));

    return result;
}

function findFreezersCabinet(freezer: CubeId): CubeId|null {
    let childIds: CubeId[] = findChildCubeIds(freezer);

    return childIds[0];
}

function findCubesInFreezer(freezer: CubeId, skip: CubeId[] = []): CubeId[] {
    let cabinet: CubeId = findFreezersCabinet(freezer);

    return findChildCubeIdsWithSkip(cabinet, skip);
}

function findFirstCubeInFreezer(freezer: CubeId, skip: CubeId[] = []): CubeId|null {
    let cabinet: CubeId = findFreezersCabinet(freezer);
    let all: CubeId[] = findChildCubeIdsWithSkip(cabinet, skip);

    return !!all.length ? all[0] : null;
}

function findCubesInTargetFreezers(freezers: CubeId[], skip: CubeId[] = []): CubeId[] {
    let result: CubeId[] = [];

    freezers.forEach((freezer: CubeId): void => {
        let cabinet: CubeId = findFreezersCabinet(freezer);
        let cur: CubeId[] = findChildCubeIdsWithSkip(cabinet, skip);

        result = result.concat(cur);
    });

    return result;
}

function findAllCabinet(): CubeId[] {
    let freezers: Array<CubeId> = findFreezer();
    let result: CubeId[] = [];

    freezers.forEach((freezer: CubeId): void => {
        let cabinet: CubeId = findFreezersCabinet(freezer);

        result.push(cabinet);
    });

    return result;
}

// 获取所有卧柜里的cube
function findAllInFreezerCubes(skip: CubeId[] = []): CubeId[] {
    let freezers: Array<CubeId> = findFreezer();
    let result: CubeId[] = [];

    freezers.forEach((freezer: CubeId): void => {
        let cabinet: CubeId = findFreezersCabinet(freezer);

        result = result.concat(findChildCubeIdsWithSkip(cabinet, skip));
    });

    return result;
}

export {
    findShelfs,
    findFreezer,
    findPrevShelf,
    findNextShelf,
    findCubeInStage,
    findNextLayer,
    findTheLastLayer,
    findPrevLayer,
    findClosestLayer,
    findClosestLayerContainSelf,
    isLastLayer,
    findSketchpad,
    getShelfCount,
    findLastShelf,
    findCubeShelf,
    shelfHasOnlyOneLayer,
    findLastPrevLayer,
    findTheFirstChildCubeId,
    findTheLastChildCubeId,
    findAllChildren,
    findCubeLayer,
    findPrevCube,
    findNextCube,
    findChildCubeByIndexSkip,
    findAllChildrenContainSelf,
    findAllChildrenContainSelfByCubeIds,
    findAllPartitions,
    findLayersCanDropInStage,
    findParentsChild,
    hasBeam,
    findCubesChildren,
    isOnlyOneShelf,
    cubeListHasShelf,
    findLayersCanDrop,
    findPrevFreezer,
    getFreezerCount,
    findCubeFreezer,
    findFreezersCabinet,
    isOnlyOneFreezer,
    findCubesInFreezer,
    findFirstCubeInFreezer,
    findAllInFreezerCubes,
    findCubesInTargetFreezers,
    findAllCabinet,
    findNextFreezer
};
